import { useEffect, useMemo, useRef, useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import ModuleDiagnostics from "@/components/athlete/ModuleDiagnostics";
import { supabase } from "@/integrations/supabase/client";
import {
  MODULE_ADVICE,
  SIMULATABLE_MODULES,
  serializeSimulation,
  simulationPreset,
  type ModuleKey,
  type PreviewSimulation,
  type ProfileModules,
} from "@/lib/profileModules";

type AthleteOption = {
  id: string;
  slug: string;
  name: string;
  sport: string | null;
  image_path: string | null;
  published: boolean;
};

const VIEWPORTS = [
  { label: "320", width: 320, note: "small phone" },
  { label: "375", width: 375, note: "iPhone SE/13 mini" },
  { label: "390", width: 390, note: "iPhone 14" },
  { label: "430", width: 430, note: "iPhone Pro Max" },
  { label: "768", width: 768, note: "tablet" },
  { label: "1024", width: 1024, note: "small laptop" },
  { label: "1440", width: 1440, note: "desktop" },
];

type CheckState = "ready" | "not-ready" | "untracked";

const StateChip = ({ state, text }: { state: CheckState; text: string }) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
      state === "ready"
        ? "bg-primary/10 text-primary"
        : state === "not-ready"
          ? "bg-muted text-muted-foreground"
          : "bg-secondary/20 text-foreground"
    }`}
  >
    {text}
  </span>
);

const AdminPreview = () => {
  const [athletes, setAthletes] = useState<AthleteOption[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [width, setWidth] = useState<number | "full">(1440);
  const [simulation, setSimulation] = useState<PreviewSimulation>({});
  const [modules, setModules] = useState<ProfileModules | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [listState, setListState] = useState<"loading" | "ok" | "auth" | "error">("loading");
  const [previewTimedOut, setPreviewTimedOut] = useState(false);
  const frameRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    let active = true;
    setListState("loading");
    supabase
      .from("athletes")
      .select("id,slug,name,sport,image_path,published")
      .order("name")
      .then(({ data, error }) => {
        if (!active) return;
        if (error) {
          const denied = /jwt|permission|denied|rls|not authorized/i.test(error.message ?? "");
          setListState(denied ? "auth" : "error");
          return;
        }
        const rows = (data ?? []) as AthleteOption[];
        setAthletes(rows);
        setSelectedId((cur) => cur || rows[0]?.id || "");
        setListState("ok");
      });
    return () => { active = false; };
  }, [refreshKey]);


  const athlete = athletes.find((a) => a.id === selectedId) ?? null;
  const simParam = serializeSimulation(simulation);
  const simulating = simParam.length > 0;
  // Admin-only, in-memory: shows the Back Her Game chooser without touching shop settings.
  const [commercePreview, setCommercePreview] = useState(false);

  const previewUrl = athlete
    ? `/athlete/${athlete.slug}?adminPreview=1${commercePreview ? "&commercePreview=1" : ""}${simParam ? `&sim=${encodeURIComponent(simParam)}` : ""}`
    : "";

  // Readiness always arrives from the live preview page itself, so this panel
  // can never disagree with what the public profile renders.
  useEffect(() => {
    setModules(null);
    setPreviewTimedOut(false);
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type !== "siis:preview-modules") return;
      if (athlete && e.data.slug !== athlete.slug) return;
      setModules(e.data.modules as ProfileModules);
      setPreviewTimedOut(false);
    };
    window.addEventListener("message", onMessage);
    // Same-origin fallback: read readiness straight off the preview window in
    // case the message fires before this listener is attached.
    const poll = window.setInterval(() => {
      const w = frameRef.current?.contentWindow as (Window & { __siisPreviewModules?: { slug: string; modules: ProfileModules } }) | null;
      const payload = w?.__siisPreviewModules;
      if (payload && (!athlete || payload.slug === athlete.slug)) {
        setModules(payload.modules);
        setPreviewTimedOut(false);
      }
    }, 700);
    // Bounded wait — the panel never sits on "Waiting for preview" forever.
    const timeout = window.setTimeout(() => setPreviewTimedOut(true), 20000);
    return () => {
      window.removeEventListener("message", onMessage);
      window.clearInterval(poll);
      window.clearTimeout(timeout);
    };
  }, [athlete?.slug, simParam, refreshKey]);

  const setModuleSim = (key: ModuleKey, value: "real" | "on" | "off") =>
    setSimulation((cur) => {
      const next = { ...cur };
      if (value === "real") delete next[key];
      else next[key] = value;
      return next;
    });

  const refresh = () => {
    setSimulation({});
    setModules(null);
    setPreviewTimedOut(false);
    setRefreshKey((k) => k + 1);
  };


  const m = modules;
  const checklist: { label: string; state: CheckState; detail: string }[] = [
    {
      label: "Core identity ready",
      state: athlete && athlete.name.trim() && athlete.sport?.trim() ? "ready" : "not-ready",
      detail: athlete?.sport?.trim() ? "Name and sport present" : "Sport missing",
    },
    {
      label: "Profile image ready",
      state: athlete?.image_path ? "ready" : "not-ready",
      detail: athlete?.image_path ? "Hero image set" : "Branded initials fallback in use",
    },
    ...(
      [
        ["Story/About ready", "about"],
        ["Quick Facts ready", "quickFacts"],
        ["Social/Connect ready", "socialLinks"],
        ["Photos ready", "photos"],
        ["Videos ready", "videos"],
        ["Causes ready", "causes"],
        ["Direct support ready", "directSupport"],
        ["Public supporter wall ready", "publicSupporters"],
        ["Brand relationships ready", "brandRelationships"],
        ["Partnership inquiry ready", "partnershipInquiry"],
      ] as [string, ModuleKey][]
    ).map(([label, key]) => ({
      label,
      state: (!m ? "untracked" : m[key].active ? "ready" : "not-ready") as CheckState,
      detail: !m ? "Waiting for preview" : m[key].active ? m[key].reason : MODULE_ADVICE[key],
    })),
    { label: "Mobile QA", state: "untracked", detail: "Not tracked — no stored field. Verify manually in the 320/390 previews." },
    { label: "Athlete approval", state: "untracked", detail: "Not tracked — no stored approval field on the athlete record." },
    {
      label: "Ready to publish",
      state: "untracked",
      detail: `Not tracked as a separate status. Current publish state: ${athlete?.published ? "Published" : "Draft"}.`,
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <header>
          <h1 className="font-display text-3xl">Athlete Profile Preview</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Renders the real public profile components. Admin only — visitors never see this tool, its diagnostics or any simulation.
          </p>
        </header>

        <div className="rounded-2xl border border-border bg-card p-4 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm font-semibold">
              <span className="block mb-1">Athlete</span>
              <select
                value={selectedId}
                onChange={(e) => { setSelectedId(e.target.value); setSimulation({}); setModules(null); }}
                className="min-h-11 rounded-xl border border-border bg-background px-3 text-sm"
              >
                {athletes.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} {a.published ? "" : "· draft"}
                  </option>
                ))}
              </select>
            </label>

            <label className="min-h-11 inline-flex items-center gap-2 rounded-full bg-muted px-4 text-sm font-semibold cursor-pointer">
              <input
                type="checkbox"
                checked={commercePreview}
                onChange={(e) => setCommercePreview(e.target.checked)}
                className="h-4 w-4"
              />
              Preview Her Collection chooser
            </label>
            <button type="button" onClick={refresh} className="min-h-11 rounded-full bg-muted px-4 text-sm font-semibold">
              Refresh (clears simulation)
            </button>
            {previewUrl && (
              <a href={previewUrl} target="_blank" rel="noreferrer" className="min-h-11 inline-flex items-center rounded-full bg-muted px-4 text-sm font-semibold">
                Open preview full size
              </a>
            )}
            {athlete?.published && (
              <a
                href={`/athlete/${athlete.slug}`}
                target="_blank"
                rel="noreferrer"
                className="min-h-11 inline-flex items-center rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground"
              >
                Open real public profile
              </a>
            )}
          </div>

          <div role="group" aria-label="Preview width" className="flex flex-wrap items-center gap-2">
            {VIEWPORTS.map((v) => (
              <button
                key={v.width}
                type="button"
                aria-pressed={width === v.width}
                onClick={() => setWidth(v.width)}
                className={`min-h-11 rounded-full px-4 text-sm font-semibold ${width === v.width ? "bg-primary text-primary-foreground" : "bg-muted"}`}
              >
                {v.label}px
              </button>
            ))}
            <button
              type="button"
              aria-pressed={width === "full"}
              onClick={() => setWidth("full")}
              className={`min-h-11 rounded-full px-4 text-sm font-semibold ${width === "full" ? "bg-primary text-primary-foreground" : "bg-muted"}`}
            >
              Full width
            </button>
            <span className="text-xs text-muted-foreground">
              {width === "full" ? "Fills the admin panel" : `${width} × 900 CSS px — real responsive rendering, no scaling`}
            </span>
          </div>
        </div>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="min-w-0 overflow-x-auto rounded-2xl border border-border bg-muted/30 p-3">
            {simulating && (
              <p className="mb-3 rounded-xl bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground">
                Simulation — not saved. Placeholder content is local to this preview only.
              </p>
            )}
            {listState === "auth" ? (
              <p className="p-6 text-sm text-muted-foreground">
                Your admin session has expired. Sign in again to use the preview.
              </p>
            ) : listState === "error" ? (
              <div className="p-6 space-y-3">
                <p className="text-sm text-muted-foreground">The athlete list didn’t load. Please try again.</p>
                <button type="button" onClick={refresh} className="min-h-11 rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground">
                  Retry
                </button>
              </div>
            ) : listState === "loading" ? (
              <p className="p-6 text-sm text-muted-foreground">Loading athletes…</p>
            ) : athlete ? (
              <>
                {previewTimedOut && !modules && (
                  <div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card px-3 py-2 text-xs">
                    <span className="font-semibold">This preview is taking too long to load.</span>
                    <button type="button" onClick={refresh} className="min-h-11 rounded-full bg-primary px-4 text-xs font-semibold text-primary-foreground">
                      Retry
                    </button>
                  </div>
                )}
                <iframe
                  key={`${athlete.slug}-${simParam}-${refreshKey}`}
                  ref={frameRef}
                  title={`Preview of ${athlete.name}`}
                  src={previewUrl}
                  style={{ width: width === "full" ? "100%" : `${width}px`, height: 900 }}
                  className="mx-auto block rounded-xl border border-border bg-background"
                />
              </>
            ) : (
              <p className="p-6 text-sm text-muted-foreground">No athletes found.</p>
            )}

          </div>

          <aside className="space-y-4">
            <section className="rounded-2xl border border-border bg-card p-4">
              <h2 className="font-display text-xl">Readiness checklist</h2>
              <ul className="mt-3 space-y-2 text-sm">
                {checklist.map((c) => (
                  <li key={c.label} className="flex flex-col gap-1 border-b border-border/60 pb-2 last:border-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold">{c.label}</span>
                      <StateChip state={c.state} text={c.state === "ready" ? "Ready" : c.state === "not-ready" ? "Not ready" : "Not tracked"} />
                    </div>
                    <span className="text-xs text-muted-foreground">{c.detail}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="rounded-2xl border border-border bg-card p-4">
              <h2 className="font-display text-xl">Simulation</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Local layout testing only. Nothing is written to the database, and the public profile is unchanged.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" onClick={() => setSimulation(simulationPreset("none"))} className="min-h-11 rounded-full bg-muted px-3 text-xs font-semibold">
                  No optional modules
                </button>
                <button type="button" onClick={() => setSimulation(simulationPreset("all"))} className="min-h-11 rounded-full bg-muted px-3 text-xs font-semibold">
                  Every module active
                </button>
                <button type="button" onClick={() => setSimulation({})} className="min-h-11 rounded-full bg-muted px-3 text-xs font-semibold">
                  Real data
                </button>
              </div>
              <ul className="mt-3 space-y-2">
                {SIMULATABLE_MODULES.map((key) => (
                  <li key={key} className="flex items-center justify-between gap-3 text-sm">
                    <span className="truncate">{m?.[key]?.label ?? key}</span>
                    <select
                      aria-label={`Simulate ${m?.[key]?.label ?? key}`}
                      value={simulation[key] ?? "real"}
                      onChange={(e) => setModuleSim(key, e.target.value as "real" | "on" | "off")}
                      className="min-h-11 rounded-xl border border-border bg-background px-2 text-xs"
                    >
                      <option value="real">Real data</option>
                      <option value="on">Force present</option>
                      <option value="off">Force absent</option>
                    </select>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-xs text-muted-foreground">
                Commerce readiness (merchandise, affiliate products) — roadmap only, not implemented and never shown publicly.
              </p>
            </section>
          </aside>
        </div>

        {m ? (
          <ModuleDiagnostics modules={m} detailed defaultOpen />
        ) : (
          <p className="text-sm text-muted-foreground">
            {previewTimedOut
              ? "Module diagnostics didn’t arrive from the preview. Use Retry above to reload it."
              : "Loading module diagnostics from the preview…"}
          </p>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminPreview;
